import type { ComponentType, LazyExoticComponent } from 'react';

export type RoleTier = 'super_admin' | 'admin' | 'editor' | 'viewer';

export type ModuleCategory =
  | 'marketing'
  | 'lifecycle'
  | 'sales'
  | 'operations'
  | 'finance'
  | 'hr'
  | 'product'
  | 'support'
  | 'other';

export interface ModuleNavLocation {
  section: 'primary' | 'admin' | 'workspace' | 'global';
  order: number;
}

/**
 * A route a module declares. `component` is narrowed to `LazyExoticComponent`
 * — a plain `ComponentType` no longer typechecks.
 *
 * WHY. Every in-house module gets its own `module-<slug>` chunk
 * (apps/web/vite.config.ts `manualChunks`), and `registry.ts` imports every
 * registered manifest eagerly so the shell can render sidebar nav. A route
 * component bound to a STATIC import therefore drags its whole module chunk
 * onto first paint for every authenticated user, regardless of access — the
 * regression #886 fixed.
 *
 * `scripts/verify-lazy-chunk-edges.mjs` guards the emitted bundle but cannot
 * see WHICH declared route a surviving lazy edge belongs to: a multi-route
 * manifest coalesces into ONE chunk, so de-lazying only `routes[0]` — the
 * single route `apps/web/src/routes/_app/modules/$slug.tsx` renders — kills
 * `/modules/<slug>` while a sibling route keeps the chunk in the
 * dynamic-import set and the gate stays green. Hence a type, not a gate.
 *
 * The two controls compose and neither replaces the other: this type proves
 * LAZY-ness, not DYNAMIC-IMPORT-ness. `lazy(async () => ({ default: Eager }))`
 * satisfies it while still statically importing the module, and that residual
 * is what the chunk-edge script catches.
 *
 * Not satisfiable by accident: `LazyExoticComponent` requires both `$$typeof`
 * and `_result`, and only `React.lazy` produces both (`memo` and `forwardRef`
 * carry `$$typeof` alone).
 *
 * If a route ever genuinely cannot be lazy, do not widen this type — that
 * re-opens it for every module manifest. Bring the case up, and expect an
 * explicitly-marked, per-route escape rather than a silent union arm.
 */
export interface ModuleRoute {
  path: string;
  component: LazyExoticComponent<ComponentType>;
  label?: string;
  hidden?: boolean;
}

/**
 * Optional per-module sidebar tree. When present, the shell sidebar
 * renders these entries inline under the module's own entry when the
 * user is inside the module. Removes the need for a second in-module
 * sidebar. Keep depth ≤3 so the 240px shell sidebar stays readable.
 */
export interface ModuleNavItem {
  label: string;
  path: string;
  /** Lucide icon name (resolved by the shell). Optional. */
  icon?: string;
  /** Minimum role tier; matches shell's RoleTier. */
  requires?: 'super_admin' | 'admin' | 'editor' | 'viewer' | 'call_center_management' | 'call_center_agents';
  /** Hide from sidebar but keep route active (e.g. :param detail pages). */
  hiddenInSidebar?: boolean;
  /** Phase tag surfaced as a badge. */
  phase?: 'v1' | 'v1.5' | 'v2' | 'v2.5' | 'v3';
  /** Nested sub-items. */
  children?: ModuleNavItem[];
}

export interface ModuleAIPrompt {
  id: string;
  label: string;
  system: string;
  description?: string;
}

/**
 * A sub-scope inside a module. Access can be granted at the feature
 * level so a user can open, say, the "CPA calculator" inside the
 * Marketing Spend module without seeing the raw campaign tables.
 */
export interface ModuleFeature {
  id: string;
  name: string;
  description: string;
  /** If true, this feature is considered sensitive and requires explicit access even when the module is granted. */
  sensitive?: boolean;
}

export interface ModuleScreenshot {
  url: string;
  caption?: string;
  /** Use redacted / sample data in the screenshot — never real customer PII. */
  redacted?: boolean;
}

export interface ModuleManifest {
  slug: string;
  name: string;
  /** One-line tagline for the Module Store card. */
  description: string;
  /** Longer paragraph shown on the public detail page. */
  longDescription?: string;
  icon: string;
  category: ModuleCategory;
  version: string;
  /** Internal owner label (e.g. 'marketing', 'ops@ezhire.ae'). */
  owner: string;
  /** Owning team — drives who approves access requests for this module. */
  ownerTeam?: string;
  /** Contact email for the module owner. */
  ownerEmail?: string;
  /** Minimum tier a user must have to request access. */
  requiredTier: RoleTier;
  nav?: ModuleNavLocation;
  routes: ModuleRoute[];
  /**
   * Plain-English capability list. Shown in the public detail page so
   * browsers can decide whether to request access. Keep bullets short.
   */
  capabilities?: string[];
  /** Screenshots (redacted sample data). Shown in the public detail page. */
  screenshots?: ModuleScreenshot[];
  /** Optional sub-scopes for fine-grained access grants. */
  features?: ModuleFeature[];
  ai?: {
    prompts: ModuleAIPrompt[];
    defaultModel?: 'claude-opus-4-7' | 'claude-sonnet-4-6' | 'claude-haiku-4-5-20251001';
  };
  dbNamespace?: string;
  /**
   * Optional tree of sub-routes the module owns. When set, the shell
   * sidebar renders these inline under the module entry when the user
   * is inside the module; the module itself no longer needs an
   * internal sidebar.
   */
  navTree?: ModuleNavItem[];
}

export function defineModule(manifest: ModuleManifest): ModuleManifest {
  return manifest;
}
